import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";

// Matches theme.ts MuiSwitch.root width — not reachable from the theme object at style-build time.
const SWITCH_WIDTH = 46;

const CHECKBOX_SIZE = 24;
const CHECKBOX_GAP = 16;

export const styles = (theme: Theme) => {
	return ({
		// Section title — Figma H5 (SF Pro Bold 18/24).
		sectionHeader: {
			fontWeight: 700,
			fontSize: 18,
			lineHeight: "24px",
			marginBottom: 8,
		},
		// Figma: section/field descriptions use primary text color (#1C1B1F), not secondary
		// (MAX-3920, MAX-3922).
		sectionDescription: {
			color: theme.palette.custom.textPrimary,
			marginTop: 4,
		},
		// Location Types section description: 24px gap below the section title (MAX-3920). Overrides
		// the shared sectionHeader mb(8)+sectionDescription mt(4) so this specific title→description
		// gap is 24px.
		locationTypesDescription: {
			marginTop: 16,
		},
		// Format description: 24px gap between the description and the radio options below it
		// (MAX-3922).
		formatDescription: {
			marginBottom: 24,
		},
		boardGroup: {
			marginTop: 24,
		},
		// Full-width divider between MLS board groups (Figma Horizontal_Divider spans the card).
		boardDivider: {
			marginBottom: 24,
		},
		// Board sub-section header — Figma H6 (SF Pro Bold 16/24), not the 18px section style (MAX-3921).
		// 24px gap below to the first checkbox row (Figma board name y=120→168). Set explicitly rather
		// than relying on the checkbox's default padding, which is now stripped for x=0 alignment.
		boardName: {
			fontWeight: 700,
			fontSize: 16,
			lineHeight: "24px",
			marginBottom: 24,
		},
		// Figma 1180:16645 — checkbox at x=0 and a single content column at x=40, so the label and
		// everything below it share one parent and stay aligned by construction.
		typeRow: {
			display: "flex",
			alignItems: "flex-start",
			gap: CHECKBOX_GAP,
			marginBottom: 24,
		},
		// Flush left with the section title / board name; the row's gap supplies the 16px.
		typeCheckboxControl: {
			padding: 0,
			width: CHECKBOX_SIZE,
			height: CHECKBOX_SIZE,
			flexShrink: 0,
		},
		typeLabel: {
			fontWeight: 700,
			// Shrink-wrap the text so only the label itself is a click target, not the whole row.
			display: "inline-block",
			cursor: "pointer",
		},
		typeDetail: {
			flex: 1,
			minWidth: 0,
		},
		// "View included locations (N)" trigger — teal/primary, SF Pro Semibold 14/16, chevron on the right.
		includedTrigger: {
			display: "inline-flex",
			alignItems: "center",
			gap: 4,
			padding: "8px 0",
			fontWeight: 590,
			fontSize: 14,
			lineHeight: "16px",
		},
		bulletList: {
			margin: "4px 0 0",
			paddingLeft: 20,
			color: theme.palette.text.secondary,
		},
		displayNameBox: {
			marginTop: 8,
			maxWidth: 400,
		},
		displayNameLabel: {
			display: "flex",
			alignItems: "center",
			gap: 6,
			marginBottom: 4,
			color: theme.palette.text.secondary,
		},
		// Field labels are 14px platform-wide (MAX-4268); body2 is 16px in this theme.
		displayNameLabelText: {
			fontSize: 14,
		},
		infoIcon: {
			display: "inline-flex",
			color: theme.palette.text.secondary,
			cursor: "help",
		},
		subSection: {
			marginTop: 24,
		},
		subSectionDivider: {
			marginTop: 24,
		},
		fieldLabel: {
			fontWeight: 700,
			marginBottom: 4,
		},
		// "Custom City List" field label sits 24px above the toggle row (MAX-3922).
		customCityListLabel: {
			fontWeight: 700,
			marginBottom: 24,
		},
		// Toggle row: 16px gap between the switch control and its label/description, and the switch
		// aligned to the center of the label line (not the center of label+description). Align the
		// control to the top so it tracks the label's first line, then push it down by half the
		// label's line-height minus half the switch height so it centers on the label text (MAX-3922).
		switchControl: {
			alignItems: "flex-start",
			marginLeft: 0,
			marginRight: 0,
			"& .MuiFormControlLabel-label": {
				marginLeft: 16,
			},
			// Center the switch on the label's first line (label line-height ~24px). Margins strip the
			// theme's default `margin: 8` so the switch sits flush at x=0 and the label lands at
			// SWITCH_WIDTH + 16.
			"& .MuiSwitch-root": {
				marginTop: 2,
				marginLeft: 0,
				marginRight: 0,
			},
		},
		switchLabel: {
			fontWeight: 700,
		},
		// Search field + pills align with the toggle's label, not the toggle — same offset as the label
		// in switchControl above (MAX-3922).
		customListDetail: {
			paddingLeft: SWITCH_WIDTH + 16,
			display: "flex",
			flexDirection: "column",
			gap: 16,
		},
		// "Clear all" — tertiary action, matching includedTrigger above.
		clearAllButton: {
			alignSelf: "flex-start",
			fontWeight: 590,
			fontSize: 14,
			lineHeight: "16px",
			padding: 0,
		},
	} satisfies Record<string, CSSObject>);
};
